import { useEffect, useState, memo, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ForumIcon from "@mui/icons-material/Forum";
import SendIcon from "@mui/icons-material/Send";
import { useSelector } from "react-redux";
import io from "socket.io-client";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCommentsQuery,
  useDeleteCommentMutation,
} from "../../queries/useToeicQueries";
import { queryKeys } from "../../queries/queryKeys";

const socket = io.connect(
  process.env.REACT_APP_SOCKET_URL || "https://lingualica.dukelewis.com"
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: "#cfe8ff" },
    "&:hover fieldset": { borderColor: "#35c759" },
    "&.Mui-focused fieldset": { borderColor: "#2196f3", borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2196f3" },
};

const timeFromNow = (createdAt) => {
  const now = moment();
  const created = moment(createdAt);
  const diffSeconds = now.diff(created, "seconds");
  const diffMinutes = now.diff(created, "minutes");
  const diffHours = now.diff(created, "hours");

  if (diffSeconds < 60) return `${diffSeconds} seconds ago`;
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  return created.format("DD/MM/YYYY HH:mm");
};

const getCommentUser = (comment) => ({
  id: comment?.comment_user?.user_id,
  name: comment?.comment_user?.user_name || "Learner",
  avatar: comment?.comment_user?.user_avatar || "",
});

const CommentCard = ({
  comment,
  onReplySubmit,
  onDeleteComment,
  onDeleteReply,
}) => {
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyText, setReplyText] = useState("");
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const userID = getUserId(currentUser);
  const isAdmin = currentUser?.metadata.user.isAdmin;
  const user = getCommentUser(comment);

  const handleReplySubmit = () => {
    if (!replyText.trim()) return;
    onReplySubmit(comment._id, replyText);
    setReplyText("");
    setReplyVisible(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 3,
        border: "1px solid #e5edf5",
        bgcolor: "#ffffff",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "44px 1fr", gap: 1.5 }}>
        <Avatar
          src={user.avatar}
          alt={user.name}
          sx={{ width: 44, height: 44, bgcolor: "#edf7ff", color: "#2196f3", fontWeight: 700 }}
        >
          {user.name.charAt(0).toUpperCase()}
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "start" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#07111f", fontWeight: 700 }}>
                {user.name}
              </Typography>
              <Typography sx={{ color: "#5f6b7a", fontSize: 13 }}>
                {timeFromNow(comment.createdAt)}
              </Typography>
            </Box>
            {(userID === user.id || isAdmin) && (
              <IconButton
                size="small"
                onClick={() => onDeleteComment(comment._id)}
                sx={{
                  bgcolor: "#fff1f1",
                  color: "#d93025",
                  "&:hover": { bgcolor: "#ffe1e1" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {comment.comment_parent_id && (
            <Chip
              size="small"
              label={`Reply to ${comment.comment_parent_user_name}`}
              sx={{ mt: 1, bgcolor: "#f0ecff", color: "#6d4aff" }}
            />
          )}

          <Typography sx={{ mt: 1.25, color: "#07111f", lineHeight: 1.7 }}>
            {comment.comment_content}
          </Typography>

          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              onClick={() => setReplyVisible((value) => !value)}
              startIcon={<ChatBubbleOutlineIcon />}
              sx={{
                color: "#2196f3",
                bgcolor: "#edf7ff",
                fontWeight: 700,
                "&:hover": { bgcolor: "#dceeff" },
              }}
            >
              Reply ({comment?.comment_replies?.length || 0})
            </Button>
          </Box>

          {replyVisible && (
            <Box sx={{ mt: 1.5 }}>
              {currentUser ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
                  <TextField
                    fullWidth
                    label="Write a reply..."
                    variant="outlined"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    sx={fieldSx}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SendIcon />}
                    onClick={handleReplySubmit}
                    sx={{ bgcolor: "#35c759", color: "#ffffff", px: 2.5, "&:hover": { bgcolor: "#2fb44f" } }}
                  >
                    Reply
                  </Button>
                </Box>
              ) : (
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#f8fbff", border: "1px dashed #cfe8ff" }}>
                  <Typography sx={{ color: "#5f6b7a", fontWeight: 800 }}>
                    Please sign in to reply.
                  </Typography>
                </Paper>
              )}

              <Box sx={{ mt: 1.5, display: "grid", gap: 1 }}>
                {comment.comment_replies?.map((reply) => {
                  const replyUser = getCommentUser(reply);

                  return (
                    <Box
                      key={reply._id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "34px 1fr auto",
                        gap: 1,
                        alignItems: "start",
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: "#f8fbff",
                        border: "1px solid #e5edf5",
                      }}
                    >
                      <Avatar
                        src={replyUser.avatar}
                        alt={replyUser.name}
                        sx={{ width: 34, height: 34, bgcolor: "#e8f9ed", color: "#1f9b45", fontSize: 14, fontWeight: 700 }}
                      >
                        {replyUser.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: "#07111f", fontWeight: 700, fontSize: 14 }}>
                          {replyUser.name}
                        </Typography>
                        <Typography sx={{ color: "#5f6b7a", fontSize: 12 }}>
                          {timeFromNow(reply.createdAt)}
                        </Typography>
                        <Typography sx={{ mt: 0.75, color: "#07111f", lineHeight: 1.6 }}>
                          <Box component="span" sx={{ color: "#d81b60", fontWeight: 700 }}>
                            @{user.name}{" "}
                          </Box>
                          {reply.comment_content}
                        </Typography>
                      </Box>
                      {(userID === replyUser.id || isAdmin) && (
                        <IconButton
                          size="small"
                          onClick={() => onDeleteReply(reply._id)}
                          sx={{ color: "#d93025" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const CommentsList = ({
  commentsData,
  onReplySubmit,
  onDeleteComment,
  onDeleteReply,
}) => {
  return commentsData.map((comment) => (
    <CommentCard
      key={comment._id}
      comment={comment}
      onReplySubmit={onReplySubmit}
      onDeleteComment={onDeleteComment}
      onDeleteReply={onDeleteReply}
    />
  ));
};

function Comments({ answerID = "66c8c95eb4bc4dbeaa469f18" }) {
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const userID = getUserId(currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const axiosJWT = createAxios(currentUser);
  const queryClient = useQueryClient();
  const { data: commentsData = [] } = useCommentsQuery(answerID);
  const deleteCommentMutation = useDeleteCommentMutation();

  const invalidateComments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.comments.byAnswer(answerID),
    });
  }, [answerID, queryClient]);

  useEffect(() => {
    socket.emit("join_room", answerID);

    socket.on("receive_comment", () => {
      invalidateComments();
    });

    socket.on("reply_comment", () => {
      invalidateComments();
    });

    socket.on("change_comment", () => {
      invalidateComments();
    });

    return () => {
      socket.off("receive_comment");
      socket.off("reply_comment");
      socket.off("change_comment");
    };
  }, [answerID, invalidateComments]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    await socket.emit("send_comment", {
      answer_id: answerID,
      user_id: userID,
      content: newComment,
      parent_comment_id: null,
    });
    setNewComment("");
  };

  const handleReplyComment = async (parentID, replyText) => {
    if (!replyText.trim()) return;

    await socket.emit("send_comment", {
      answer_id: answerID,
      user_id: userID,
      content: replyText,
      parent_comment_id: parentID,
    });
  };

  const handleDeleteComment = useCallback(
    async (commentID) => {
      setLoading(true);
      const data = { answer_id: answerID, comment_id: commentID };
      await deleteCommentMutation.mutateAsync({
        accessToken,
        userID,
        data,
        axiosJWT,
      });
      socket.emit("delete_comment", data);
      setLoading(false);
    },
    [accessToken, answerID, axiosJWT, deleteCommentMutation, userID]
  );

  const handleDeleteReply = async (replyID) => {
    setLoading(true);
    const data = { answer_id: answerID, comment_id: replyID };
    await deleteCommentMutation.mutateAsync({
      accessToken,
      userID,
      data,
      axiosJWT,
    });
    socket.emit("delete_comment", data);
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") handleAddComment();
  };

  return (
    <Box sx={{ width: "100%", mx: "auto" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: "1px solid #e5edf5",
          bgcolor: "#f8fbff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2, display: "grid", placeItems: "center", bgcolor: "#edf7ff", color: "#2196f3" }}>
              <ForumIcon />
            </Box>
            <Box>
              <Typography sx={{ color: "#07111f", fontWeight: 700 }}>Discussion</Typography>
              <Typography sx={{ color: "#5f6b7a", fontSize: 13 }}>
                {commentsData.length} comments
              </Typography>
            </Box>
          </Box>
          <Chip size="small" label="Live" sx={{ bgcolor: "#e8f9ed", color: "#1f9b45" }} />
        </Box>

        {currentUser ? (
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr auto" }, gap: 1 }}>
            <TextField
              fullWidth
              label="Add a comment..."
              variant="outlined"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              sx={fieldSx}
            />
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleAddComment}
              sx={{ bgcolor: "#35c759", color: "#ffffff", px: 3, "&:hover": { bgcolor: "#2fb44f" } }}
            >
              Post
            </Button>
          </Box>
        ) : (
          <Paper elevation={0} sx={{ p: 2, textAlign: "center", borderRadius: 2, border: "1px dashed #cfe8ff", bgcolor: "#ffffff" }}>
            <Typography sx={{ color: "#5f6b7a", fontWeight: 800 }}>
              Please sign in to comment.
            </Typography>
          </Paper>
        )}
      </Paper>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {commentsData.length ? (
        <CommentsList
          commentsData={commentsData}
          onReplySubmit={handleReplyComment}
          onDeleteComment={handleDeleteComment}
          onDeleteReply={handleDeleteReply}
        />
      ) : (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: 3, border: "1px dashed #cfe8ff", bgcolor: "#ffffff" }}>
          <ForumIcon sx={{ color: "#2196f3", fontSize: 44, mb: 1 }} />
          <Typography sx={{ color: "#07111f", fontWeight: 700 }}>
            No comments yet
          </Typography>
          <Typography sx={{ color: "#5f6b7a" }}>
            Start the discussion after reviewing this test.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default memo(Comments);
